"use client";

/**
 * `FileField` — single-file upload control with both a click-to-browse
 * button and a drag-and-drop target. The persisted form-state value is
 * a `File` object (or `null` when nothing is selected); the engine does
 * NOT upload bytes — the caller's `onSubmit` is responsible for whatever
 * the server contract is (FormData, presigned URL PUT, multipart, etc.).
 *
 * Why a custom drop zone rather than the native file input alone:
 *
 * - The native `<input type="file">` has zero drag-and-drop affordance
 *   on its own. Users expect a visible target they can drop onto, so we
 *   render a styled rectangle and forward drops into the same state
 *   path as click-to-browse.
 * - The browse button is the keyboard / screen-reader entry point. We
 *   keep the real file input in the DOM (visually hidden, NOT
 *   `display:none`) so that label-association still works and assistive
 *   tech can drive it. Clicking the visible "Browse" button proxies to
 *   the input's `.click()`.
 * - The visible chrome (drop zone + selected-file row) is the
 *   "interactive" surface; the input is the underlying control.
 *
 * Drag-state UX: the drop zone tracks `isDraggingOver` from
 * `dragenter` / `dragleave` / `dragover` events and renders an accent
 * border + tinted bg while the cursor holds a draggable file. We
 * `preventDefault` on `dragover` so the browser allows the drop (the
 * default browser behaviour for a file dropped on most elements is to
 * navigate to it).
 *
 * Selected state: filename + human-readable size + a `Remove` button
 * stacked next to it. Clicking remove sets the form value to `null` and
 * clears the file input's `.value` so re-selecting the same file fires
 * a fresh change event (browsers suppress the change event when the
 * selected file is identical to the previous one).
 *
 * `maxSize` validation runs on selection before we commit to form
 * state. If the file exceeds the cap, we render an inline error
 * underneath the drop zone (using the same red-text pattern the rest of
 * the engine uses) and DO NOT replace any previously-selected file.
 * This is intentional — partially accepting a too-big file would force
 * the caller to re-validate themselves.
 *
 * `accept` is plumbed straight to the native input. Browsers honour it
 * for the file picker but NOT for drops; a user can drop any file type
 * regardless of the `accept` filter. We keep this consistent with
 * native behaviour rather than rolling our own MIME check on drop.
 *
 * Test note: jsdom 25 does not expose a real file picker, so the test
 * harness sets `input.files` manually and dispatches a change event.
 * That codepath is the same one the real browser uses, so the field
 * behaves identically in tests and production.
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { Upload, X } from "lucide-react";
import { cn } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { FileFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface FileFieldProps {
  field: FileFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

/**
 * Format a byte count as a human-readable string.
 *
 * Uses binary (1024-based) units to match what every desktop OS shows
 * in its file properties dialog — RSD invoices and accounting users
 * routinely cross-reference filesizes with their OS file managers, so
 * "1 KB = 1024 bytes" is the right reference frame.
 *
 * Output rounds to one decimal for KB and above, no decimals for B.
 * Examples: `0` → "0 B", `512` → "512 B", `1024` → "1 KB", `1536` →
 * "1.5 KB", `1048576` → "1 MB", `5242880` → "5 MB".
 *
 * Negative inputs and non-finite numbers are coerced to `0 B` rather
 * than thrown — the field's own validation already rejects oversized
 * files; this helper is purely a display formatter and shouldn't
 * crash on weird inputs.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  if (bytes < 1024) return `${String(Math.round(bytes))} B`;
  const kb = bytes / 1024;
  if (kb < 1024) {
    // Drop the trailing ".0" for whole numbers — "2 KB" reads cleaner
    // than "2.0 KB" while a fractional "1.5 KB" still gets its decimal.
    const rounded = Math.round(kb * 10) / 10;
    return Number.isInteger(rounded) ? `${String(rounded)} KB` : `${rounded.toFixed(1)} KB`;
  }
  const mb = kb / 1024;
  if (mb < 1024) {
    const rounded = Math.round(mb * 10) / 10;
    return Number.isInteger(rounded) ? `${String(rounded)} MB` : `${rounded.toFixed(1)} MB`;
  }
  const gb = mb / 1024;
  const rounded = Math.round(gb * 10) / 10;
  return Number.isInteger(rounded) ? `${String(rounded)} GB` : `${rounded.toFixed(1)} GB`;
}

export function FileField({ field, pathPrefix }: FileFieldProps): React.ReactElement | null {
  const { form } = useFormContext();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  // Inline size-error state. Lives outside form state because rhf's
  // errors map is owned by the zod resolver — a maxSize rejection is a
  // UX-layer rule the caller didn't ask zod to enforce, so we surface
  // it locally without polluting `formState.errors`.
  const [sizeError, setSizeError] = React.useState<string | undefined>(
    undefined,
  );

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const formError = readErrorMessage(form.formState.errors, path);
  const errorMessage = sizeError ?? formError;
  const isDisabled = field.disabled === true;

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Controller
        name={path}
        control={form.control}
        render={({ field: rhf }) => {
          // The form-state value can be `File`, `null`, or `undefined`
          // (when defaultValues left the slot empty). Normalise to the
          // narrowest type the renderer cares about.
          const selected =
            rhf.value instanceof File ? (rhf.value as File) : null;

          /**
           * Common acceptance path: validate size, then commit.
           * Returns `true` when committed, `false` when rejected.
           */
          const acceptFile = (file: File): boolean => {
            if (
              typeof field.maxSize === "number" &&
              file.size > field.maxSize
            ) {
              setSizeError(
                `File ${file.name} (${formatBytes(file.size)}) exceeds the ${formatBytes(field.maxSize)} limit.`,
              );
              return false;
            }
            setSizeError(undefined);
            rhf.onChange(file);
            return true;
          };

          const handleInputChange = (
            event: React.ChangeEvent<HTMLInputElement>,
          ) => {
            const file = event.target.files?.[0];
            if (!file) return;
            const accepted = acceptFile(file);
            // If we rejected, clear the input so the user can pick the
            // same file again (some browsers don't fire `change` when
            // the same file is reselected). If we accepted, leave the
            // input alone — its `.files` reflect what was committed.
            if (!accepted && inputRef.current) {
              inputRef.current.value = "";
            }
          };

          const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDraggingOver(false);
            if (isDisabled) return;
            const file = event.dataTransfer.files?.[0];
            if (!file) return;
            acceptFile(file);
          };

          const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            // The browser default drops files by navigating to them; the
            // explicit "copy" effect tells the OS we'll consume it.
            event.dataTransfer.dropEffect = "copy";
          };

          const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (!isDisabled) {
              setIsDraggingOver(true);
            }
          };

          const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDraggingOver(false);
          };

          const remove = () => {
            rhf.onChange(null);
            setSizeError(undefined);
            if (inputRef.current) {
              inputRef.current.value = "";
            }
          };

          const browse = () => {
            if (isDisabled) return;
            inputRef.current?.click();
          };

          // The native file input is rendered ALWAYS so the
          // `<Label htmlFor>` association stays valid through both
          // empty and selected states. Visually we hide it inside the
          // currently-active surface (dropzone or selected card) via
          // an absolute-positioned overlay; click-to-browse still works
          // because the visible "Browse" button proxies to `input.click()`.
          const hiddenInput = (
            <input
              id={path}
              type="file"
              ref={(el) => {
                inputRef.current = el;
                rhf.ref(el);
              }}
              onChange={handleInputChange}
              onBlur={rhf.onBlur}
              aria-invalid={errorMessage !== undefined ? true : undefined}
              disabled={isDisabled}
              {...(field.accept !== undefined ? { accept: field.accept } : {})}
              className="sr-only"
              tabIndex={-1}
            />
          );

          return (
            <div className="space-y-1.5">
              {hiddenInput}
              {selected ? (
                <div
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-bg-subtle px-3 py-2 text-sm text-fg"
                  data-testid="file-selected"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{selected.name}</span>
                    <span className="text-xs text-fg-muted">
                      {formatBytes(selected.size)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={remove}
                    disabled={isDisabled}
                    aria-label={`Remove file ${selected.name}`}
                    className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-muted hover:bg-bg hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X aria-hidden="true" className="size-4" />
                  </button>
                </div>
              ) : (
                <div
                  data-testid="file-dropzone"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  className={cn(
                    "relative flex flex-col items-center justify-center gap-2 rounded-md border border-dashed bg-bg-subtle px-4 py-8 text-center text-sm text-fg-muted",
                    "transition-colors duration-fast ease-standard",
                    isDraggingOver
                      ? "border-accent bg-accent/5 text-fg"
                      : "border-border",
                    errorMessage !== undefined && "border-danger",
                    isDisabled && "cursor-not-allowed opacity-50",
                  )}
                >
                  <Upload aria-hidden="true" className="size-6" />
                  <div className="flex flex-col items-center gap-1">
                    <p>
                      <button
                        type="button"
                        onClick={browse}
                        disabled={isDisabled}
                        className="font-medium text-accent-strong underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Browse
                      </button>
                      <span> or drag and drop</span>
                    </p>
                    {field.placeholder !== undefined ? (
                      <p className="text-xs">{field.placeholder}</p>
                    ) : null}
                    {typeof field.maxSize === "number" ? (
                      <p className="text-xs">
                        Max size: {formatBytes(field.maxSize)}
                      </p>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          );
        }}
      />
    </FieldShell>
  );
}
